import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import { Button, Field, Textarea } from "@/components/ui-kit";

const MINIMO = 10;

/**
 * Exclusão de produto — que é duas coisas diferentes conforme o histórico.
 *
 * O diálogo PERGUNTA ao servidor antes de mostrar qualquer botão. Sem isso, o
 * único jeito de descobrir que o cadastro tem venda seria clicar em "Excluir" e
 * levar um erro — e a pessoa só entenderia que precisava de justificativa depois
 * de já ter tentado. Aqui a tela abre dizendo o que vai acontecer e por quê,
 * com os números na frente.
 *
 * SEM HISTÓRICO o produto sai do banco: nada a preservar, e o slug e os códigos
 * de barras voltam a ficar livres para o cadastro certo.
 *
 * COM HISTÓRICO ele some da loja e do balcão, mas a linha fica. Não é escolha
 * de projeto — a venda aconteceu, a NFC-e foi declarada à SEFAZ, e o item
 * precisa continuar resolvível. A justificativa fica gravada ao lado do fato,
 * com quem excluiu e quando.
 */
export default function DeleteProductDialog({ product, onClose, onDeleted }) {
  const [info, setInfo] = useState(null);
  const [erro, setErro] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [excluindo, setExcluindo] = useState(false);

  useEffect(() => {
    let vivo = true;
    setInfo(null); setErro(false); setMotivo("");
    api.get(`/admin/products/${product.id}/deletion-info`)
      .then((r) => vivo && setInfo(r.data))
      .catch(() => vivo && setErro(true));
    return () => { vivo = false; };
  }, [product.id]);

  const precisaMotivo = info?.has_history;
  const motivoCurto = motivo.trim().length < MINIMO;

  const excluir = async () => {
    setExcluindo(true);
    try {
      await api.delete(`/admin/products/${product.id}`, {
        // O corpo só existe quando há histórico: mandar `reason` num produto
        // sem venda faria o servidor guardar justificativa de uma linha que ele
        // vai apagar de qualquer forma.
        data: precisaMotivo ? { reason: motivo.trim() } : undefined,
      });
      toast.success(precisaMotivo
        ? `${product.name} foi excluído. O histórico de vendas continua no sistema.`
        : `${product.name} excluído`);
      onDeleted();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setExcluindo(false); }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Excluir ${product.name}`}
      testId="delete-product-dialog"
      footer={info && (
        <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:gap-3 sm:justify-end">
          <Button variant="ghost" onClick={onClose} className="h-11 sm:h-9">Voltar</Button>
          <Button
            variant="danger"
            icon={Trash2}
            loading={excluindo}
            disabled={precisaMotivo && motivoCurto}
            onClick={excluir}
            data-testid="delete-product-confirm"
            className="h-11 sm:h-9"
          >
            {precisaMotivo ? "Excluir com justificativa" : "Excluir"}
          </Button>
        </div>
      )}
    >
      {erro && <p className="text-sm text-red-600">Não foi possível verificar o histórico deste produto. Tente de novo.</p>}
      {!info && !erro && <p className="text-sm text-slate-400 py-4">Verificando o histórico do produto…</p>}

      {info && !info.has_history && (
        <p className="text-sm text-slate-600 leading-relaxed">
          Este produto <b>nunca vendeu e não tem movimentação de estoque</b>. Ele sai do
          cadastro de vez, junto com variações, fotos e avaliações. O código de barras e o
          endereço dele na loja voltam a ficar livres.
        </p>
      )}

      {info?.has_history && (
        <div className="space-y-4">
          <div className="flex gap-3 rounded-lg bg-amber-50 border border-amber-200 p-3">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <div className="text-sm text-amber-900 leading-relaxed">
              <p className="font-semibold">Este produto tem histórico.</p>
              {/* Os números na frente: é a diferença entre "tem histórico" e
                  "foi vendido 14 vezes", que muda a decisão de quem está ali. */}
              <p className="mt-1">
                {[
                  info.pos_sales > 0 && `${info.pos_sales} ${info.pos_sales === 1 ? "item vendido no balcão" : "itens vendidos no balcão"}`,
                  info.orders > 0 && `${info.orders} ${info.orders === 1 ? "item em pedido da loja" : "itens em pedidos da loja"}`,
                  info.stock_movements > 0 && `${info.stock_movements} ${info.stock_movements === 1 ? "movimentação de estoque" : "movimentações de estoque"}`,
                ].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>

          <p className="text-sm text-slate-600 leading-relaxed">
            Ele <b>sai da loja, do PDV e das listagens</b>, mas as vendas continuam intactas —
            a nota fiscal já emitida precisa seguir apontando para este item. O código de
            barras é liberado para você recadastrar o produto se precisar.
          </p>

          <Field
            label="Por que este produto está sendo excluído?"
            hint="Fica registrado com seu nome e a data. Quem for conferir isso depois não vai ter a quem perguntar."
          >
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              maxLength={300}
              autoFocus
              placeholder="Ex.: cadastro duplicado pela importação de XML; a loja passou a usar o outro registro."
              data-testid="delete-product-reason"
            />
          </Field>
          {/* Contador só quando falta: aviso permanente vira ruído que ninguém lê. */}
          {motivo.length > 0 && motivoCurto && (
            <p className="text-xs text-amber-600 -mt-2">
              Faltam {MINIMO - motivo.trim().length} caracteres.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
